import React, {ReactNode} from "react";

// global style, it's very IMPORTANT that this should load first
// before loading any other component
import "./src/styles/common.scss";

import type {GatsbyBrowser} from "gatsby";
import {initSentry, logErrorToSentry} from "./src/utils/sentry";
import {updateUtmCookies} from "./src/utils/UTM-tracking";
import {logEvent} from "./src/utils/amplitude";
import ErrorBoundary from "./src/components/error-boundary/ErrorBoundary";
import GDPRWrapper from "./src/components/gdpr/wrapper/GDPRWrapper";
import FlexportAsciiShip from "./src/constants/flexport-ascii-ship";
import {logPath} from "./src/utils/path-logging";
import WindowSizeProvider from "./src/contexts/window-context";
import {AlgoliaContext} from "./src/contexts/context";
import {logDataLayerEvent} from "./src/utils/gtm-data-layer";
import {isBot} from "./src/utils/utils";
import {smoothScrollToElement, runWhenIdle} from "./src/utils/scroll";

import {
  GATSBY_DATADOG_CLIENT_TOKEN,
  GATSBY_DATADOG_APPLICATION_ID,
  NODE_ENV,
} from "./src/config";

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({
  element,
}: {
  element: ReactNode;
}) => {
  return (
    <ErrorBoundary>
      <GDPRWrapper>
        <WindowSizeProvider>{element}</WindowSizeProvider>
      </GDPRWrapper>
    </ErrorBoundary>
  );
};

// Import the shared wrapPageElement from gatsby-shared
import {wrapPageElement as sharedWrapPageElement} from "./gatsby-shared";

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props,
}: {
  element: ReactNode;
  props: any;
}) => {
  // First wrap with shared scripts, then add browser-specific wrapping
  const wrappedElement = (
    <>
      {/*
        passing along algolia context here
        grab it from the page context
        this way, we don't need to create new env prefixed with GATSBY_
      */}
      <AlgoliaContext.Provider value={props.pageContext.algoliaIndex!}>
        {element}
      </AlgoliaContext.Provider>
    </>
  );

  // Apply shared wrapping with scripts
  return sharedWrapPageElement({element: wrappedElement, props});
};

let gtmEventTimeoutId: ReturnType<typeof setTimeout> | null = null;

// called when gatsby client runtime first starts https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#onClientEntry
export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  initSentry();

  // Handle Atlas navigation by dispatching custom event with URL
  const handleAtlasNavigation = (currentPath: string) => {
    const atlasBrowserNavigationEvent = new CustomEvent(
      "atlasBrowserNavigation",
      {
        detail: {
          url: window.location.href,
          pathname: currentPath,
          source: "browser-back-forward",
        },
      }
    );

    window.dispatchEvent(atlasBrowserNavigationEvent);
  };

  // Block Gatsby's handling of browser back/forward navigation for Atlas routes
  const blockAtlasPopstateEvents = () => {
    // Override the popstate event handling for Atlas routes
    const originalAddEventListener = window.addEventListener;

    // Override addEventListener to intercept Gatsby's popstate listeners
    window.addEventListener = function (
      type: string,
      listener: any,
      options?: any
    ) {
      if (type === "popstate") {
        // Check if this looks like a Gatsby listener (they usually don't have names or have Gatsby-specific patterns)
        // listenerString like ()=>{n=s(e),t({location:n,action:"POP"})}
        const listenerString = listener.toString();
        if (listenerString.includes("location")) {
          // Wrap the listener to block Atlas routes and handle Atlas navigation
          const wrappedListener = (event: Event) => {
            const currentPath = window.location.pathname;
            if (currentPath.startsWith("/atlas")) {
              // Handle Atlas navigation directly here
              handleAtlasNavigation(currentPath);
              return; // Don't call Gatsby's listener
            }
            // Call original listener for non-Atlas routes
            return listener.call(this, event);
          };

          return originalAddEventListener.call(
            this,
            type,
            wrappedListener,
            options
          );
        }
      }
      // For non-popstate events or non-Gatsby listeners, use original
      return originalAddEventListener.call(this, type, listener, options);
    };
  };

  // Call the function to set up the blocking
  blockAtlasPopstateEvents();

  // Handle smooth scrolling for anchor links without affecting page navigation
  const handleAnchorLinks = () => {
    document.addEventListener("click", event => {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a");

      if (!anchor) return;

      const href = anchor.getAttribute("href");

      // Check if it's an anchor link (starts with #)
      if (href && href.startsWith("#")) {
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          event.preventDefault();
          smoothScrollToElement(targetElement);

          // Update URL without triggering navigation
          if (window.history && window.history.pushState) {
            window.history.pushState(null, "", href);
          }
        }
      }
    });
  };

  handleAnchorLinks();
};

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] =
  () => {
    try {
      updateUtmCookies();
    } catch (error) {
      logErrorToSentry(
        error instanceof Error ? error : new Error(String(error))
      );
    }

    if (NODE_ENV !== "production") return;

    const isRobot = isBot(navigator.userAgent);

    runWhenIdle(async () => {
      try {
        const {datadogRum} = await import("@datadog/browser-rum-slim");
        datadogRum.init({
          applicationId: GATSBY_DATADOG_APPLICATION_ID!,
          clientToken: GATSBY_DATADOG_CLIENT_TOKEN!,
          site: "datadoghq.com",
          service: "flexport-static-site",
          env: "production",
          defaultPrivacyLevel: "mask-user-input",
          sessionSampleRate: isRobot ? 0 : 100,
        });
        // Removed `startSessionReplayRecording()`, not included in slim version
        const {setRum} = await import("./src/utils/datadog-rum");
        setRum(datadogRum);
        // Move to idle to avoid triggering Sentry
        if (process.env.NODE_ENV === "production") {
          /* eslint-disable-next-line no-console */
          console.log(FlexportAsciiShip);
        }
      } catch {}
    });
  };

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({
  location,
}: {
  location: Location;
}) => {
  logEvent("gatsbyRouteUpdate", {path: location.pathname});
  logPath(location.pathname);

  if (gtmEventTimeoutId) {
    clearTimeout(gtmEventTimeoutId);
  }

  // schedule heavier analytics on idle
  const fireAnalyticEvents = () => {
    try {
      logDataLayerEvent("gatsbyRouteLoaded", {}, NODE_ENV != "production");
    } catch {}
    gtmEventTimeoutId = null;
  };

  // Use idle instead to improve INP
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(fireAnalyticEvents, {timeout: 800});
  } else {
    gtmEventTimeoutId = setTimeout(fireAnalyticEvents, 300);
  }

  // Handle smooth scroll for hash links on route update
  if (location.hash) {
    const targetId = location.hash.substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      // Use RAF to ensure DOM has been painted before scrolling
      smoothScrollToElement(targetElement, true);
    }
  }
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: {location},
  getSavedScrollPosition,
}) => {
  const state = (location.state ?? {}) as {preventScrollReset?: boolean};

  if (state.preventScrollReset) {
    // tell Gatsby NOT to manage scrolling for this navigation
    return false;
  }

  // If there's a hash in the URL, let the smooth scroll handler take care of it
  if (location.hash) {
    return false;
  }

  // For all other navigation events, use Gatsby's default behavior
  const savedPosition = getSavedScrollPosition(location);
  return savedPosition ?? [0, 0];
};
